import { set } from 'lodash'
import { useEffect, useState } from 'react'

import { ApiFee } from '../../../../../api/_legacy/generated-api'
import {
  Fees,
  FeesInput,
  FormItem,
  Label,
  Section,
  Select,
  Space,
  Typography,
  ValidationMessage
} from '../../../../../components/core-v2'
import { Attribute, TradeApprove } from '../../../../../components/core-v2/molecules'
import TradeApproveReadonly from '../../../../../components/core-v2/molecules/trade-approve-readonly'
import TradeEventDisplay from '../../../../../components/core-v2/organisms/trade-event-display/trade-event-display'
import { formatFees } from '../../../../tradebuilder-v2/util'
import { filterFeeTypes } from '../../../../user-admin/common'
import { SetEconomicsFormState, SetEconomicsStepState } from '../state/set-economics-slice'

export type SetEconomicsStatelessProps = {
  onSetValid?: () => void
  onTradeApprove: (formState: SetEconomicsFormState) => void
  onTradeCancel?: () => void
  state: SetEconomicsStepState
}

export function isErrorWith(errors: { [key: string]: string }, fieldName: string) {
  return !!errors[fieldName]
}

const SetEconomicsStateless = ({
  onSetValid,
  onTradeApprove,
  state
}: SetEconomicsStatelessProps) => {
  const [economicsForm, setEconomicsForm] = useState<SetEconomicsFormState>(state.formState)
  const { errors } = state
  const availableDepositInstruments = state.depositInstruments?.map(item => item.instrumentId)
  const availableLoanInstruments = state.loanInstruments?.map(item => item.instrumentId)
  const availableCptyIds = state.counterpartyIds
  const { availableFeeTypes } = state
  const { strategyTemplateId } = state.formState
  const { hasBankGuarantee } = state.formState
  const isEditable =
    state.userType === 'admin' ||
    (state.userType === 'absa' &&
      (state.userRoles.includes('BankAdmin') || state.userRoles.includes('FLStructuring')))

  useEffect(() => {
    setEconomicsForm(state.formState)
  }, [state.formState])

  const availableFeeBases = ['TotalTradeValue', 'RiskyAssetExposure', 'LoanAmount']
  const availableFeeFreqs = ['Quarterly', 'Monthly', 'Annually']

  const onUpdateNoteField = (property: string, value: string | ApiFee[]) => {
    if (economicsForm) {
      onSetValid()
      const cloned = { ...economicsForm }
      set(cloned, property, value)
      setEconomicsForm(cloned)
    }
  }
  const onApprove = () => {
    onTradeApprove(economicsForm)
  }
  const onChangeFeeProperty = (
    feesProperty: string,
    items: ApiFee[],
    index: number,
    propertyName: string,
    value: any
  ) => {
    const cloned = items.map(fee => ({ ...fee } as ApiFee))
    set(cloned[index], propertyName, value)
    onUpdateNoteField(feesProperty, cloned)
  }
  const onAddNewFee = (feesProperty: string, items: ApiFee[], newFee: ApiFee) => {
    const cloned = [...items, newFee]
    onUpdateNoteField(feesProperty, cloned)
  }
  const onRemoveExistingFee = (feesProperty: string, items: ApiFee[], indexToRemove: number) => {
    const cloned = [...items]
    cloned.splice(indexToRemove, 1)
    onUpdateNoteField(feesProperty, cloned)
  }

  const renderCompletedState = () => (
    <>
      <Space mt={6}>
        <Attribute name="Counterparty ID">
          <Typography variant="b3">{economicsForm.counterPartyId}</Typography>
        </Attribute>
        <Attribute name="Deposit instrument">
          <Typography variant="b3">{economicsForm.depositId}</Typography>
        </Attribute>
        <Attribute name="Loan instrument">
          <Typography variant="b3">{economicsForm.loanId}</Typography>
        </Attribute>
        {economicsForm.noteFees && (
          <Space mt={6}>
            <Section p={3} type="primary">
              <Fees displayCptyId fees={formatFees(economicsForm.noteFees)} title="Note charge" />
            </Section>
          </Space>
        )}
        {economicsForm.portfolioFees && (
          <Space mt={5}>
            <Section p={3} type="primary">
              <Fees
                displayCptyId
                fees={formatFees(economicsForm.portfolioFees)}
                title="Portfolio charge"
              />
            </Section>
          </Space>
        )}
        {economicsForm.strategyFees && (
          <Space mt={5}>
            <Section p={3} type="primary">
              <Fees
                displayCptyId
                fees={formatFees(economicsForm.strategyFees)}
                title="Strategy charge"
              />
            </Section>
          </Space>
        )}
      </Space>
      <Space>
        {state.approvalEvents.map(approvalEvent => (
          <TradeEventDisplay key={approvalEvent.userId} {...approvalEvent} />
        ))}
      </Space>
    </>
  )

  const renderPendingState = () => (
    <Space mt={6}>
      <FormItem>
        <Label>Counterparty Id</Label>
        <Select
          value={economicsForm.counterPartyId}
          onChange={v => onUpdateNoteField('counterPartyId', v.target.value)}
        >
          <option value="">Select a counterparty Id</option>
          {availableCptyIds.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <ValidationMessage errors={errors} fieldName="cptyId" />
      </FormItem>

      <FormItem>
        <Label>Deposit instrument</Label>
        <Select
          value={economicsForm.depositId}
          onChange={v => onUpdateNoteField('depositId', v.target.value)}
        >
          <option value="">Select a deposit instrument</option>
          {availableDepositInstruments.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <ValidationMessage errors={errors} fieldName="depositId" />
      </FormItem>

      <FormItem>
        <Label>Loan instrument</Label>
        <Select
          value={economicsForm.loanId}
          onChange={v => onUpdateNoteField('loanId', v.target.value)}
        >
          <option value="">Select a loan instrument</option>
          {availableLoanInstruments.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <ValidationMessage errors={errors} fieldName="loanId" />
      </FormItem>

      <Space mt={5}>
        <Section pb={1} pt={3} px={1} type="primary">
          {!state.isExistingNote && (
            <FeesInput
              availableCptyIds={availableCptyIds}
              availableFeeBases={availableFeeBases}
              availableFeeFreqs={availableFeeFreqs}
              availableFeeTypes={filterFeeTypes(availableFeeTypes, 'note')}
              displayFees={economicsForm.noteFees}
              feeCategory="note"
              title="Note charge"
              onAddNewFee={(newFee: ApiFee) =>
                onAddNewFee('noteFees', economicsForm.noteFees, newFee)
              }
              onFeePropertyChange={(index, property, updatedValue) =>
                onChangeFeeProperty(
                  'noteFees',
                  economicsForm.noteFees,
                  index,
                  property,
                  updatedValue
                )
              }
              onRemoveExistingFee={(index: number) =>
                onRemoveExistingFee('noteFees', economicsForm.noteFees, index)
              }
            />
          )}
          {state.isExistingNote && (
            <Fees displayCptyId fees={formatFees(economicsForm.noteFees)} title="Note charge" />
          )}
        </Section>
      </Space>

      <Space mt={5}>
        <Section pb={1} pt={3} px={1} type="primary">
          <FeesInput
            availableCptyIds={availableCptyIds}
            availableFeeBases={availableFeeBases}
            availableFeeFreqs={availableFeeFreqs}
            availableFeeTypes={filterFeeTypes(availableFeeTypes, 'portfolio')}
            displayFees={economicsForm.portfolioFees}
            feeCategory="portfolio"
            title="Portfolio charge"
            onAddNewFee={(newFee: ApiFee) =>
              onAddNewFee('portfolioFees', economicsForm.portfolioFees, newFee)
            }
            onFeePropertyChange={(index, property, updatedValue) =>
              onChangeFeeProperty(
                'portfolioFees',
                economicsForm.portfolioFees,
                index,
                property,
                updatedValue
              )
            }
            onRemoveExistingFee={(index: number) =>
              onRemoveExistingFee('portfolioFees', economicsForm.portfolioFees, index)
            }
          />
        </Section>
      </Space>

      <Space mt={5}>
        <Section pb={1} pt={3} px={1} type="primary">
          <FeesInput
            availableCptyIds={availableCptyIds}
            availableFeeBases={availableFeeBases}
            availableFeeFreqs={availableFeeFreqs}
            availableFeeTypes={filterFeeTypes(
              availableFeeTypes,
              strategyTemplateId,
              hasBankGuarantee
            )}
            displayFees={economicsForm.strategyFees}
            feeCategory="strategy"
            title="Strategy charge"
            onAddNewFee={(newFee: ApiFee) =>
              onAddNewFee('strategyFees', economicsForm.strategyFees, newFee)
            }
            onFeePropertyChange={(index, property, updatedValue) =>
              onChangeFeeProperty(
                'strategyFees',
                economicsForm.strategyFees,
                index,
                property,
                updatedValue
              )
            }
            onRemoveExistingFee={(index: number) =>
              onRemoveExistingFee('strategyFees', economicsForm.strategyFees, index)
            }
          />
        </Section>
        <ValidationMessage errors={errors} fieldName="feeRates" />
      </Space>

      <Space mt={5}>
        <ValidationMessage
          errors={errors}
          excludeFieldNames={['cptyId', 'depositId', 'loanId', 'feeRates']}
        />
      </Space>
      <TradeApprove
        approveBtnCaption="Approve"
        approvingSection="set economics"
        canApprove={state.canProceed}
        userName={state.userName}
        onApprove={onApprove}
      />
    </Space>
  )

  const renderReadOnly = () => (
    <Space my={6}>
      <TradeApproveReadonly waitingDescription="This trade is currently awaiting its fees and charges to be finalised. Please check back later" />
    </Space>
  )

  return (
    <>
      {state.hasCompleted && renderCompletedState()}
      {!state.hasCompleted && isEditable && renderPendingState()}
      {!state.hasCompleted && !isEditable && renderReadOnly()}
    </>
  )
}

export default SetEconomicsStateless
